/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useAgentStatus } from './hooks/useAgentStatus';
import { STATUS_COLORS as statusColor } from '../../shared/components/atoms/status-indicator';
import { Switch } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { QSTypography } from '../../shared/components/atoms';

export default function AgentAvailabilityChatList() {
  const { isOnline, handleOpenMoreMenu, changeStatus, isMoreMenuOpen } = useAgentStatus();
  const [shouldShow, setShouldShow] = useState(!isOnline);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousIsOnlineRef = useRef(isOnline);

  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // User went from offline to online
    if (!previousIsOnlineRef.current && isOnline) {
      setShouldShow(true);

      // Start countdown to hide after 1 second
      timeoutRef.current = setTimeout(() => {
        setShouldShow(false);
      }, 1000);
    }
    // User went from online to offline
    else if (previousIsOnlineRef.current && !isOnline) {
      setShouldShow(true);
    }

    previousIsOnlineRef.current = isOnline;

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isOnline]);

  const toggle = () => changeStatus(!isOnline);

  return (
    <div
      onClick={toggle}
      css={css({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingLeft: '16px',
        paddingRight: '7px',
        backgroundColor: isOnline ? `#f7fbf9` : `#fdf7f7`,
        zIndex: 1000,
        width: '100%',
        overflow: 'hidden',
        cursor: 'pointer',
        maxHeight: shouldShow ? '100px' : '0',
        opacity: shouldShow ? 1 : 0,
        paddingTop: shouldShow ? '16px' : '0',
        paddingBottom: shouldShow ? '16px' : '0',
        transform: shouldShow ? 'translateY(0)' : 'translateY(-10px)',
        transition: 'all 0.5s cubic-bezier(0.4, 0.0, 0.2, 1), background-color 0.3s ease',
      })}
    >
      <div
        css={css({
          display: 'flex',
          alignItems: 'flex-start',
          flexDirection: 'column',
          gap: '2px',
          width: '100%',
        })}
      >
        <QSTypography
          css={css`
            font-weight: 600;
            font-size: 14px;
            line-height: 19px;
            letter-spacing: 0%;
            vertical-align: middle;
            color: ${isOnline ? statusColor.ONLINE : '#EF5350'};
          `}
        >
          {isOnline ? 'You are now online' : 'Your status is set to offline'}
        </QSTypography>
        <QSTypography
          css={css`
            font-weight: 400;
            font-size: 12px;
            line-height: 15px;
            letter-spacing: 0%;
            vertical-align: middle;
            color: #666666;
          `}
        >
          {!isOnline ? 'New chats will not be assigned to you' : 'New chats will be assigned to you'}
        </QSTypography>
      </div>
      <Switch checked={isOnline} onChange={toggle} onClick={(e) => e.stopPropagation()} size="small" />
    </div>
  );
}
